import { useState, useRef, useCallback } from "react";
import {
    ArrowUpRight,
    Search,
    Megaphone,
    Palette,
    LineChart,
    Video,
    Code,
} from "lucide-react";

const services = [
    {
        icon: Palette,
        title: "Brand & Identity",
        blurb: "Wordmarks, systems and story engines built to survive founder mode.",
        bullets: ["Positioning", "Identity", "Web design"],
    },
    {
        icon: Video,
        title: "Content Engine",
        blurb: "A weekly content rhythm that compounds — scripts, edits, captions, distribution.",
        bullets: ["Short-form", "Long-form", "Repurposing"],
    },
    {
        icon: Megaphone,
        title: "Paid & Growth",
        blurb: "Performance creative that thinks in tests, not campaigns. Meta, TikTok, Google.",
        bullets: ["Paid social", "UGC ads", "Landing pages"],
    },
    {
        icon: Search,
        title: "SEO Analytics",
        blurb: "Technical SEO, content clusters and dashboards that read like a P&L.",
        bullets: ["Audits", "Content", "Reporting"],
    },
    {
        icon: LineChart,
        title: "Data & CRO",
        blurb: "Instrumentation, funnels and A/B tests that turn hunches into revenue.",
        bullets: ["Tracking", "Experiments", "Insights"],
    },
    {
        icon: Code,
        title: "Web & Software",
        blurb: "Engineered marketing sites, portals and product surfaces — headless, fast.",
        bullets: ["Next.js", "CMS", "Product UI"],
    },
];

export default function DigtekServices() {
    const [active, setActive] = useState(0);
    const hoverTimer = useRef(null);
    const activeRef = useRef(0);

    // Debounced + deduped active setter — prevents rapid-fire state churn
    const setActiveDebounced = useCallback((i) => {
        if (activeRef.current === i) return;
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
        hoverTimer.current = setTimeout(() => {
            activeRef.current = i;
            setActive(i);
        }, 220);
    }, []);

    const handleClick = useCallback((i) => {
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
        activeRef.current = i;
        setActive(i);
    }, []);

    return (
        <section
            id="services"
            data-testid="digtek-services"
            className="relative py-24 lg:py-32 bg-white overflow-hidden"
        >
            <div className="digtek-blob purple w-[320px] h-[320px] -left-24 top-1/2 opacity-40" />
            <div className="digtek-blob lime w-[300px] h-[300px] -right-24 top-24 opacity-40" />

            <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
                {/* Header */}
                <div className="grid grid-cols-1 lg:grid-cols-12 items-end gap-8 mb-14">
                    <div className="lg:col-span-8">
                        <div className="digtek-eyebrow mb-4">
                            <span>Why brands work with us</span>
                        </div>
                        <h2
                            data-testid="services-heading"
                            className="font-display font-bold text-[42px] sm:text-[52px] lg:text-[64px] leading-[1.02] text-digtek-header"
                        >
                            One studio.
                            <br />
                            Six ways to{" "}
                            <em className="not-italic text-digtek-purple">outgrow</em>{" "}
                            your category.
                        </h2>
                    </div>
                    <div className="lg:col-span-4 flex lg:justify-end">
                        <a
                            href="#contact"
                            data-testid="services-cta"
                            className="btn-digtek-purple"
                        >
                            Explore more
                            <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </div>
                </div>

                {/* Interactive expandable panel row */}
                <div
                    data-testid="services-panels"
                    className="digtek-panels-row"
                    onMouseLeave={() => {
                        if (hoverTimer.current) clearTimeout(hoverTimer.current);
                    }}
                >
                    {services.map((s, i) => {
                        const Icon = s.icon;
                        const isActive = i === active;
                        const isSecondary = i === (active + 1) % services.length;
                        const state = isActive
                            ? "active"
                            : isSecondary
                              ? "secondary"
                              : "collapsed";
                        return (
                            <button
                                type="button"
                                key={s.title}
                                data-testid={`service-panel-${i}`}
                                data-state={state}
                                onMouseEnter={() => setActiveDebounced(i)}
                                onFocus={() => setActiveDebounced(i)}
                                onClick={() => handleClick(i)}
                                className={`digtek-panel digtek-panel-${state}`}
                                aria-label={s.title}
                                aria-pressed={isActive}
                            >
                                {/* Number badge (always rendered, position shifts by state) */}
                                <span aria-hidden className="digtek-panel-number">
                                    0{i + 1}
                                </span>

                                {/* ---- ACTIVE layer ---- */}
                                <div className="digtek-panel-layer digtek-panel-layer-active">
                                    <div className="flex flex-wrap gap-2 max-w-[80%] relative z-[2]">
                                        {s.bullets.map((b) => (
                                            <span key={b} className="digtek-chip">
                                                {b}
                                                <ArrowUpRight className="w-3 h-3" />
                                            </span>
                                        ))}
                                    </div>
                                    <div className="digtek-panel-illu">
                                        <Icon
                                            className="w-[150px] h-[150px] sm:w-[190px] sm:h-[190px]"
                                            strokeWidth={1.1}
                                        />
                                    </div>
                                    <div className="digtek-panel-title-row">
                                        <div className="w-2.5 h-2.5 rounded-full bg-digtek-lime shrink-0" />
                                        <span
                                            className="text-white text-[22px] sm:text-[26px] leading-tight"
                                            style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif', fontWeight: 700 }}
                                        >
                                            {s.title}
                                        </span>
                                    </div>
                                </div>

                                {/* ---- SECONDARY layer ---- */}
                                <div className="digtek-panel-layer digtek-panel-layer-secondary">
                                    <div className="digtek-icon-chip !w-[56px] !h-[56px] !rounded-[16px] mb-5">
                                        <Icon className="w-7 h-7" strokeWidth={1.6} />
                                    </div>
                                    <h3
                                        className="text-digtek-header mb-2 text-xl"
                                        style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif', fontWeight: 700 }}
                                    >
                                        {s.title}
                                    </h3>
                                    <p
                                        className="text-digtek-text text-[13px] leading-relaxed mb-4"
                                        style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif', fontWeight: 500 }}
                                    >
                                        {s.blurb}
                                    </p>
                                    <span
                                        className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-digtek-purple"
                                        style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif', fontWeight: 700 }}
                                    >
                                        Read more
                                        <ArrowUpRight className="w-4 h-4" />
                                    </span>
                                </div>

                                {/* ---- COLLAPSED layer ---- */}
                                <div className="digtek-panel-layer digtek-panel-layer-collapsed">
                                    <span className="digtek-panel-vertical-title">
                                        {s.title}
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
